import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, CircularProgress, IconButton,
  Select, MenuItem, FormControl, InputLabel, TextField, InputAdornment,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { toast } from 'react-toastify';
import { fetchCampaigns, pauseCampaign, resumeCampaign, cancelCampaign, getCampaign } from '../api/emailApi';
import CampaignCard from '../components/CampaignCard';
import CampaignDrawer from '../components/CampaignDrawer';

const STAT_CARDS = [
  { key: 'total', label: 'Total Campaigns', gradient: 'linear-gradient(135deg, #6366f1, #8b5cf6)', icon: PeopleIcon },
  { key: 'active', label: 'Active', gradient: 'linear-gradient(135deg, #10b981, #34d399)', icon: PlayArrowIcon },
  { key: 'paused', label: 'Paused', gradient: 'linear-gradient(135deg, #f59e0b, #fbbf24)', icon: PauseIcon },
  { key: 'completed', label: 'Completed', gradient: 'linear-gradient(135deg, #6366f1, #818cf8)', icon: CheckCircleIcon },
  { key: 'totalEmailsSent', label: 'Emails Sent', gradient: 'linear-gradient(135deg, #8b5cf6, #a78bfa)', icon: MailOutlineIcon },
];

export default function Campaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const botId = localStorage.getItem('selectedBotId') || '';

  const stats = useMemo(() => {
    const total = campaigns.length;
    const active = campaigns.filter(c => c.status === 'active').length;
    const paused = campaigns.filter(c => c.status === 'paused').length;
    const completed = campaigns.filter(c => c.status === 'completed').length;
    const totalEmailsSent = campaigns.reduce((sum, c) => sum + (c.sentEmails?.length || 0), 0);
    return { total, active, paused, completed, totalEmailsSent };
  }, [campaigns]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [allRes, _filteredRes] = await Promise.all([
        fetchCampaigns({ botId }),                        // all for stats
        statusFilter ? fetchCampaigns({ botId, status: statusFilter }) : Promise.resolve(null),
      ]);
      if (allRes?.ok) setCampaigns(allRes.campaigns);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [botId, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let list = campaigns;
    if (statusFilter) list = list.filter(c => c.status === statusFilter);
    if (search) list = list.filter(c =>
      c.contactName?.toLowerCase().includes(search.toLowerCase()) ||
      c.contactEmail?.toLowerCase().includes(search.toLowerCase())
    );
    return list;
  }, [campaigns, statusFilter, search]);

  const openDrawer = async (campaign) => {
    setDetailLoading(true);
    setDrawerOpen(true);
    try {
      const res = await getCampaign(campaign._id);
      if (res?.ok) setSelected(res.campaign);
    } catch { setSelected(campaign); }
    setDetailLoading(false);
  };

  const handleAction = async (campaign, action) => {
    let r;
    if (action === 'pause') r = await pauseCampaign(campaign._id);
    else if (action === 'resume') r = await resumeCampaign(campaign._id);
    else if (action === 'cancel') r = await cancelCampaign(campaign._id);
    if (r?.ok) {
      toast.success(`Campaign ${action}d`);
      load();
      if (selected?._id === campaign._id) {
        const updated = await getCampaign(campaign._id);
        if (updated?.ok) setSelected(updated.campaign);
      }
    } else {
      toast.error(r?.error || `Failed to ${action} campaign`);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827' }}>Email Campaigns</Typography>
          <Typography sx={{ color: '#6b7280', fontSize: 13, mt: 0.3 }}>
            Track and manage automated email sequences for your leads.
          </Typography>
        </Box>
      </Box>

      {/* Stats Dashboard */}
      {stats && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
          {STAT_CARDS.map(s => {
            const Icon = s.icon;
            const value = stats[s.key] ?? 0;
            return (
              <Box key={s.key} sx={{
                background: s.gradient, borderRadius: 3, p: 2.5, color: '#fff',
                display: 'flex', flexDirection: 'column', gap: 0.5,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-2px)' },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, opacity: 0.9 }}>{s.label}</Typography>
                  <Icon sx={{ fontSize: 20, opacity: 0.8 }} />
                </Box>
                <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1 }}>
                  {typeof value === 'number' ? value.toLocaleString() : value}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Toolbar */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small" placeholder="Search by name or email..."
          value={search} onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#9ca3af' }} /></InputAdornment>,
            sx: { borderRadius: 2, fontSize: 13 },
          }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel sx={{ fontSize: 13 }}>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={e => setStatusFilter(e.target.value)} sx={{ borderRadius: 2, fontSize: 13 }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="paused">Paused</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
        <IconButton onClick={load} sx={{ bgcolor: '#f9fafb', borderRadius: 2, '&:hover': { bgcolor: '#f3f4f6' } }}>
          <RefreshIcon sx={{ fontSize: 20 }} />
        </IconButton>
        {filtered.length > 0 && (
          <Typography sx={{ fontSize: 12, color: '#9ca3af', ml: 'auto' }}>
            {filtered.length} campaign{filtered.length !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>

      {/* Campaign List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <MailOutlineIcon sx={{ fontSize: 56, color: '#d1d5db', mb: 2 }} />
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#6b7280', mb: 0.5 }}>
            {search || statusFilter ? 'No campaigns match your filter' : 'No campaigns yet'}
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#9ca3af', mb: 3, maxWidth: 400, mx: 'auto' }}>
            {search || statusFilter
              ? 'Try adjusting your search or filter to find what you\'re looking for.'
              : 'Create an email sequence in Settings → Email Automation to start sending automated emails to your leads.'
            }
          </Typography>
          {!search && !statusFilter && (
            <Button variant="contained" onClick={() => window.location.hash = '#/settings/email-automation'}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
              Create Sequence
            </Button>
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2 }}>
          {filtered.map(c => (
            <CampaignCard key={c._id} campaign={c} onSelect={(campaign, action) => {
              if (action) handleAction(campaign, action);
              else openDrawer(campaign);
            }} />
          ))}
        </Box>
      )}

      {/* Detail Drawer */}
      <CampaignDrawer
        open={drawerOpen}
        campaign={selected}
        loading={detailLoading}
        onClose={() => { setDrawerOpen(false); setSelected(null); }}
        onAction={(action) => selected && handleAction(selected, action)}
      />
    </Box>
  );
}
